import { ErrorBoundary } from '@fox/extensions/src/error-boundary';
import { FlexRowSpaced } from '@fox/layout/src/flex';
import { useTheme } from '@mui/material';
import type { IPlacement } from 'rc-drawer/lib/IDrawerPropTypes';
import React, {
	cloneElement,
	Dispatch,
	ReactElement,
	ReactNode,
	SetStateAction,
	Suspense,
	useEffect,
	useLayoutEffect,
	useMemo,
	useRef,
	useState
} from 'react';
import styled from 'styled-components';
import { LoadingDotsCenter } from '../animated-foxes/index';
import { Divider } from '../divider/index';
import { IconButton, IconButtonProps } from '../icon-button/index';
import { Icon, IconName, IconProps } from '../icon/index';
import { ListItemIcon, ListItemText, MuiMenuItem, MuiMenuList } from '../mui-list/index';
import { usePopoverContext } from '../popover/hooks';
import { IPopoverProps, Popover } from '../popover/index';
import { ConfirmPopover, DeletePopover } from '../popover/popovers';
import { ResponsiveDrawer, ResponsiveDrawerProps } from '../responsive-drawer/index';
import { Spin } from '../spin/index';
import { Typography } from '../typography/index';

const ColumnWrapper = styled.div`
	display: flex;
	justify-content: space-evenly;
`;

const ActionPopover = {
	delete: DeletePopover,
	confirm: ConfirmPopover
};
export type ConfirmMode = keyof typeof ActionPopover;

export type ActionsMenuOption =
	| {
			key: string;
			color?: IconProps['color'];
			/** The icon to be displayed */
			iconName: IconName;

			/** The label for this menu item */
			label: string;

			/** A component to render at the end of the Drawer Header */
			labelEndAdornment?: ReactNode;

			/** A function that will execute the menu item's purpose */
			action?: () => void | Promise<void>;

			/** For actions that require confirmation, a message that is displayed before the action is called */
			confirm?: {
				mode: ConfirmMode;
				message: React.ReactNode;
				acceptTitle?: string;
			};

			/** For actions that should have a child menu */
			childPopover?: React.ReactNode;

			/** Where full control is required, this will replace the body of the menu item entirely */
			childComponent?: React.ReactNode;

			/** The placement for the menu */
			placement?: string;

			/** A menu item that will render a drawer */
			drawer?: React.ReactElement;

			drawerWidth?: string | number;
			/** the placement of a drawer that is a child of a menu item */
			drawerPlacement?: IPlacement;

			order?: number;

			disabled?: boolean;

			/**
			 * Fully close all menus when this item is selected
			 * @default true */
			fullyClose?: boolean;
	  }
	| 'divider';

// PLEASE BE AWARE - WE ARE USING HERE CUSTOM HOOKS TO BUILD ACTION MENU OPTIONS! WHEN USING THEM WE WANT TO ENSURE WE ARE FOLLOWING THE RULES OF HOOKS ⚓️
// BUILD CONTEXT MENU OCCURES WITHIN A RENDER
export function useDidMount() {
	const didMountRef = useRef(true);

	useEffect(() => {
		didMountRef.current = true;
		return () => void (didMountRef.current = false);
	}, []);
	return didMountRef;
}

export type ActionMenuPopoverProps = {
	options: Array<ActionsMenuOption>;
	mainIconSize?: IconButtonProps['size'];
	placement?: IPopoverProps['placement'];
	childPopoverPlacement?: IPopoverProps['placement'];
};

/**
 * Prefer {@link ActionMenu} as that will allow the use of drawers in a menu.
 *
 * @param options - menu options with actions / popover & other props you want to see under 3-dot menu
 * @param mainIconSize - optional property in case if you want to change the size of main 3-dot icon
 * @returns React fragment - 3 dot menu with options you specified on click
 */
export const ActionMenuPopover: React.FC<ActionMenuPopoverProps> = ({ options, mainIconSize, placement = 'right', childPopoverPlacement }) => {
	if (options?.length < 1) {
		return <></>;
	}
	return (
		<ErrorBoundary>
			<ColumnWrapper>
				<Popover placement={placement} overlay={<ActionMenuList options={options} childPopoverPlacement={childPopoverPlacement} />}>
					<div onClick={e => e.stopPropagation()}>
						<IconButton title="Settings" iconName="MoreVert" size={mainIconSize} />
					</div>
				</Popover>
			</ColumnWrapper>
		</ErrorBoundary>
	);
};

export const ActionMenuList = ({ options, childPopoverPlacement }: ActionMenuPopoverProps) => {
	const [mutating, setMutating] = useState<string | null>(null);
	const didMount = useDidMount();

	return (
		<div onClick={e => e.stopPropagation()}>
			<MuiMenuList
				sx={{
					padding: theme => `${theme.shape['radius-sm']}px`,
					'& .MuiMenuItem-root': {
						margin: 0,
						width: '100%'
					}
				}}
			>
				{options.map((option: ActionsMenuOption) => {
					if (option === 'divider') return <Divider sx={{ margin: theme => `${theme.shape['radius-sm']}px 0` }} />;

					const Something = ActionPopover[option.confirm?.mode] ?? React.Fragment;
					const childComponent = option.childComponent;
					if (childComponent) {
						return (
							<Spin spinning={mutating === option.key} key={option.key}>
								{childComponent}
							</Spin>
						);
					}

					const action = async () => {
						setMutating(option.key);
						await option?.action?.();
						if (didMount.current) setMutating(null);
					};

					const confirmProps = {
						message: option.confirm?.message,
						confirm: action,
						trigger: 'click',
						placement: option.placement ? option.placement : 'topRight'
					};

					const childProps = option.childPopover;

					if (childProps) {
						return (
							<Spin spinning={mutating === option.key} key={option.key}>
								{/* I really feel that this should just be able to be "auto", but it is used in so many places and I am paranoid*/}
								<Popover placement={childPopoverPlacement ?? 'left-end'} overlay={childProps}>
									<div key={option.key}>
										<ActionMenuOptions option={option} childProps={childProps} action={action} />
									</div>
								</Popover>
							</Spin>
						);
					}

					return (
						<Spin spinning={mutating === option.key} key={option.key}>
							<Something {...(option.confirm?.mode ? confirmProps : null)} key={option.key}>
								<ActionMenuOptions option={option} action={action} />
							</Something>
						</Spin>
					);
				})}
			</MuiMenuList>
		</div>
	);
};

const ActionMenuOptions = (props: { option: Exclude<ActionsMenuOption, 'divider'>; action?: () => void; childProps?: React.ReactNode }) => {
	const popoverContext = usePopoverContext();
	const color = props.option.color;
	const theme = useTheme();
	// TODO: waiting on confirmation from UX/UI re: error colours, manually implementing this for now.
	const isError = color === 'error';
	const errorColor = theme.palette.mode === 'dark' ? theme.palette.error.light : theme.palette.error.main;
	const labelColor = isError ? errorColor : theme.palette.text.primary;
	const optionDisabled = props.option?.disabled;

	return (
		<MuiMenuItem
			disabled={optionDisabled}
			value={props.option.key}
			onClick={async () => {
				if (!props.option.confirm?.message && !props.childProps) {
					await props.action?.();
					popoverContext.closePopover(props.option.fullyClose ?? true);
				}
			}}
		>
			<ListItemIcon>
				<Icon
					fontSize="small"
					iconName={props.option.iconName}
					color={isError ? undefined : (color ?? 'inherit')}
					customColor={isError ? errorColor : undefined}
				/>
			</ListItemIcon>
			<ListItemText primaryTypographyProps={{ color: labelColor }}>
				<>{props.option.label}</>
			</ListItemText>
		</MuiMenuItem>
	);
};

type ActionMenuProps = Omit<ActionMenuPopoverProps, 'options'> & {
	options: Array<ActionsMenuOption>;
};

/** Improved version of the ActionPopoverMenu that supports activating a drawer from a menu item */
export function ActionMenu(props: ActionMenuProps) {
	// only render our custom version if there are drawers
	if (!props.options.some(s => typeof s !== 'string' && s.drawer)) return <ActionMenuPopover {...props} />;

	return <ActionMenuWithDrawer {...props} />;
}

export type DrawerNodeState =
	| ({
			/** The node for the responsive drawer contents
			 */ node: ReactElement;
	  } & Pick<
			Partial<ResponsiveDrawerProps>,
			'title' | 'description' | 'width' | 'maxWidthPercent' | 'closable' | 'placement' | 'destroyOnClose' | 'descriptionVariant'
	  >)
	| null;

export function useDrawerNode() {
	return useState<DrawerNodeState>(null);
}

export function DrawerNode({
	drawer,
	setDrawer,
	overrideProps
}: {
	drawer: DrawerNodeState;
	setDrawer: Dispatch<SetStateAction<DrawerNodeState>>;
	overrideProps?: unknown;
}) {
	const [lastWidth, setLastWidth] = useState({ width: drawer?.width, maxWidthPercent: drawer?.maxWidthPercent });
	useLayoutEffect(() => {
		// The purpose of this effect is to capture the last intended width of the drawer. It will always defer to drawer.Width and being a layoutEffect it should update state prior to the screen updating.
		// This prevents on close the width jumping due to us losing the value
		if (drawer) {
			setLastWidth({ width: drawer?.width, maxWidthPercent: drawer?.maxWidthPercent });
		}
	}, [drawer]);
	return (
		<ResponsiveDrawer
			open={Boolean(drawer)}
			placement={drawer?.placement}
			closable={drawer?.closable}
			title={drawer?.title}
			destroyOnClose={drawer?.destroyOnClose}
			description={drawer?.description}
			descriptionVariant={drawer?.descriptionVariant}
			onClose={() => setDrawer(null)}
			width={drawer?.width ?? lastWidth.width}
			maxWidthPercent={drawer?.maxWidthPercent ?? lastWidth.maxWidthPercent}
		>
			<Suspense fallback={<LoadingDotsCenter />}>
				{drawer?.node && overrideProps ? cloneElement(drawer.node, overrideProps) : drawer?.node}
			</Suspense>
		</ResponsiveDrawer>
	);
}
const useActionMenuDrawer = (options: ActionMenuProps['options']) => {
	const [drawer, setDrawer] = useDrawerNode();

	const o = useMemo(() => {
		return options.map(o => {
			if (typeof o === 'string') return o;
			const { drawer, drawerWidth, drawerPlacement, ...a } = o;
			return drawer
				? {
						...a,
						action: async () => {
							// Execute the action, if it is defined
							if (a.action) await a.action();

							const title = Boolean(a.labelEndAdornment) ? (
								<FlexRowSpaced>
									<Typography>{a.label}</Typography>
									{a.labelEndAdornment}
								</FlexRowSpaced>
							) : (
								a.label
							);

							setDrawer({ node: drawer, title, width: drawerWidth, placement: drawerPlacement });
						}
					}
				: a;
		});
	}, options);
	return [o, drawer, setDrawer] as const;
};

function ActionMenuWithDrawer({ mainIconSize, options }: ActionMenuProps) {
	const [o, drawer, setDrawer] = useActionMenuDrawer(options);

	return (
		<div onClick={e => e.stopPropagation()}>
			<DrawerNode drawer={drawer} setDrawer={setDrawer} />
			<ActionMenuPopover mainIconSize={mainIconSize} options={o} />
		</div>
	);
}

export function ActionMenuListWithDrawer({ options }: ActionMenuProps) {
	const [o, drawer, setDrawer] = useActionMenuDrawer(options);

	return (
		<div onClick={e => e.stopPropagation()}>
			<DrawerNode drawer={drawer} setDrawer={setDrawer} />
			<ActionMenuList options={o} />
		</div>
	);
}
